/**
 * @fileoverview Login page component.
 * @summary User login page with email and password.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/LoginPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../hooks/redux.js'
import { login, clearError } from '../store/slices/authSlice.js'
import { useRoleBasedRedirect } from '../hooks/useRoleBasedRedirect.js'
import { ADMIN_DASHBOARD_URL } from '../config/constants.js'
import FloatingLabelInput from '@/components/FloatingLabelInput.js'
import Spinner from '@/components/Spinner.js'

/**
 * @summary Login page component.
 * @returns {React.ReactElement} Login page.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const { isLoading, error } = useAppSelector((state) => state.auth)

  const redirectParam = searchParams.get('redirect') || null

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Redirect if already authenticated
  useRoleBasedRedirect(true, redirectParam)

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError())
    }
  }, [dispatch])

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!email.trim()) {
      errors.email = t('errors.required')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t('errors.invalidEmail')
    }

    if (!password) {
      errors.password = t('errors.required')
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      const loggedInUser = await dispatch(login({ email, password })).unwrap()
      // Redirect based on redirect param or user role
      // Note: The hook will handle the redirect, but we need to trigger it manually after login
      if (redirectParam) {
        navigate(redirectParam, { replace: true })
      } else if (loggedInUser.role === 'superadmin') {
        window.location.href = ADMIN_DASHBOARD_URL
      } else if (loggedInUser.role === 'organizer') {
        window.location.href = '/dashboard'
      } else if (loggedInUser.role === 'singer') {
        window.location.href = '/singer/dashboard'
      } else {
        window.location.href = '/dashboard'
      }
    } catch (err) {
      // Error is handled by Redux slice
    }
  }

  return (
    <div className="min-h-screen flex pt-8 justify-center  ">
      <div className="w-full page-container">
        <div className="card">
          <div className="text-center mb-8 mt-4">
            {/* <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-gradient">
              KaraoNow
            </h1> */}
            <h2 className="text-xl sm:text-2xl font-semibold text-(--color-text-secondary)">
              {t('auth.login')}
            </h2>
            {/* <p className="mt-2 text-sm text-gray-600">
              Connectez-vous pour gérer vos événements karaoke
            </p> */}
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-error">
                <div className="text-sm font-medium">{error}</div>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <FloatingLabelInput
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  label={t('auth.email')}
                />
                
                {formErrors.email && (
                  <p className="mt-1 text-sm text-error">{formErrors.email}</p>
                )}
              </div>

              <div>
                
                <FloatingLabelInput
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  label={t('auth.password')}
                />
                {formErrors.password && (
                  <p className="mt-1 text-sm text-error">{formErrors.password}</p>
                )}
              </div>
            </div>

            <div >
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full btn-lg"
              >
                {isLoading ? (
                  <Spinner size="sm" variant="white" />
                ) : (
                  t('auth.loginButton')
                )}
              </button>
            </div>

            <div className="space-y-4 mb-4">
              <p className="text-sm text-(--color-text-tertiary) ">
                <Link
                  to="/forgot-password"
                  className="font-semibold link-primary"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </p>
              <p className="text-sm text-(--color-text-tertiary)">
                {t('auth.dontHaveAccount')}{' '}
                <Link
                  to="/register"
                  className="font-semibold link-primary"
                >
                  {t('auth.register')}
                </Link>
              </p>
              
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
